import { Container, VectorInput, Label } from '@playcanvas/pcui';
import { Quat, RotateGizmo, Vec3 } from 'playcanvas';

import { Pivot } from 'src/pivot';

import { TransformTool } from './transform-tool';
import { Events } from '../events';
import { Scene } from '../scene';


const v = new Vec3();

class RotateTool extends TransformTool {
    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const gizmo = new RotateGizmo(scene.camera.entity.camera, scene.gizmoLayer);

        // ui
        const rotateToolbar = new Container({
            id: 'rotate-toolbar',
            hidden: true
        });

        rotateToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        const axis = new Container({
            class: 'transform-row'
        });


        const xLabel = new Label({
            class: ['transform-expand', 'transform-label', 'transform-axis-label'],
            text: 'x'
        });

        const yLabel = new Label({
            class: ['transform-expand', 'transform-label', 'transform-axis-label'],
            text: 'y'
        });

        const zLabel = new Label({
            class: ['transform-expand', 'transform-label', 'transform-axis-label'],
            text: 'z'
        });

        axis.append(xLabel);
        axis.append(yLabel);
        axis.append(zLabel);

        const rotation = new Container({
            class: 'transform-row'
        });

        const rotationVector = new VectorInput({
            class: 'transform-expand',
            precision: 2,
            dimensions: 3,
            value: [0, 0, 0],
            enabled: false
        });

        rotation.append(rotationVector);

        rotateToolbar.append(axis);
        rotateToolbar.append(rotation);
        canvasContainer.append(rotateToolbar);

        const toArray = (v: Vec3) => {
            return [v.x, v.y, v.z];
        };

        let uiUpdating = false;
        let mouseUpdating = false;

        // update UI with pivot
        const updateUI = (pivot: Pivot) => {
            uiUpdating = true;
            const transform = pivot.transform;
            transform.rotation.getEulerAngles(v);
            rotationVector.value = toArray(v);
            uiUpdating = false;
        };

        // update pivot with UI
        const updatePivot = (pivot: Pivot) => {
            const transform = pivot.transform;
            const p = transform.position;
            const r = rotationVector.value;
            const q = new Quat().setFromEulerAngles(r[0], r[1], r[2]);
            const s = transform.scale;

            if (q.w < 0) {
                q.mulScalar(-1);
            }

            pivot.moveTRS(p, q, s);

        };

        // handle a change in the UI state
        const change = () => {
            if (!uiUpdating) {
                const pivot = events.invoke('pivot') as Pivot;
                if (mouseUpdating) {
                    updatePivot(pivot);
                } else {
                    pivot.start();
                    updatePivot(pivot);
                    pivot.end();
                }
            }
        };

        const mousedown = () => {
            mouseUpdating = true;
            const pivot = events.invoke('pivot') as Pivot;
            pivot.start();
        };

        const mouseup = () => {
            const pivot = events.invoke('pivot') as Pivot;
            updatePivot(pivot);
            mouseUpdating = false;
            pivot.end();
        };

        [rotationVector.inputs].flat().forEach((input) => {
            input.on('change', change);
            input.on('slider:mousedown', mousedown);
            input.on('slider:mouseup', mouseup);
        });

        // toggle ui availability based on selection
        events.on('selection.changed', (selection) => {
            rotationVector.enabled = !!selection;
        });

        events.on('pivot.placed', (pivot: Pivot) => {
            updateUI(pivot);
        });

        events.on('pivot.moved', (pivot: Pivot) => {
            if (!mouseUpdating) {
                updateUI(pivot);
            }
        });

        events.on('pivot.ended', (pivot: Pivot) => {
            updateUI(pivot);
        });

        super(gizmo, events, scene, rotateToolbar);

    }
}

export { RotateTool };
